import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { useLandingPageData } from '../context/LandingPageContext';

// SEO configurations for each page variant
const seoConfig = {
  allgemein: {
    de: {
      title: 'Haartransplantation Mönchengladbach ab 2.999€ | FUE Saphir, DHI & Long Hair FUE | Dion Hair Clinic',
      description: 'Haartransplantation in Mönchengladbach ab 2.999€. FUE Saphir, DHI Hybrid (ohne Rasur Oberkopf) & Long Hair FUE (komplett ohne Rasur). 12 Monate Nachsorge, 2 regenerative Therapien inklusive. Ratenzahlung ab 53€/Monat, 6 Monate zinsfrei. Kostenlose Beratung ✓ 4.9★',
      keywords: 'Haartransplantation, Haartransplantation Mönchengladbach, Haartransplantation NRW, Haartransplantation Deutschland, Haartransplantation Düsseldorf, Haartransplantation Köln, FUE Haartransplantation, DHI Haartransplantation, Saphir FUE, Long Hair FUE, Haartransplantation ohne Rasur, Haartransplantation Kosten, Haarverpflanzung, Eigenhaartransplantation, Geheimratsecken, Haarausfall Behandlung, Haartransplantation Ratenzahlung, Haartransplantation Erfahrungen, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/',
    },
    en: {
      title: 'Hair Transplant Germany from 2,999€ | FUE Sapphire, DHI & Long Hair FUE | Dion Hair Clinic',
      description: 'Hair transplant in Germany from 2,999€. FUE Sapphire, DHI Hybrid (no shave top) & Long Hair FUE (completely no shave). 12 months aftercare included. Installments from 53€/month. Free consultation ✓ 4.9★',
      keywords: 'Hair Transplant, Hair Transplant Germany, Hair Transplant NRW, FUE Hair Transplant, DHI Hair Transplant, Sapphire FUE, Long Hair FUE, Hair Transplant without Shaving, Hair Transplant Cost Germany, Hair Restoration, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/?lang=en',
    }
  },
  koeln: {
    de: {
      title: 'Haartransplantation Köln & Umgebung ab 2.999€ | FUE Saphir & DHI | Dion Hair Clinic',
      description: 'Haartransplantation nahe Köln ab 2.999€. Nur 30 Min. von Köln entfernt. FUE Saphir, DHI & Long Hair FUE. 12 Monate Nachsorge. Ratenzahlung möglich. Kostenlose Beratung für Patienten aus Köln, Düsseldorf & NRW.',
      keywords: 'Haartransplantation Köln, Haartransplantation NRW, Haartransplantation Düsseldorf, FUE Köln, DHI Köln, Haarausfall Köln, Haarverpflanzung Köln, Haarklinik Köln, Haartransplantation Kosten Köln, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/koeln',
    },
    en: {
      title: 'Hair Transplant Cologne Region from 2,999€ | FUE & DHI | Dion Hair Clinic',
      description: 'Hair transplant near Cologne from 2,999€. Only 30 min from Cologne. FUE Sapphire, DHI & Long Hair FUE. Free consultation for patients from Cologne, Düsseldorf & NRW.',
      keywords: 'Hair Transplant Cologne, Hair Transplant Germany, FUE Cologne, DHI Cologne, Hair Loss Cologne, Hair Restoration Cologne, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/koeln?lang=en',
    }
  },
  dhi: {
    de: {
      title: 'DHI Haartransplantation ohne Rasur | Saphir-DHI Hybrid ab +800€ | Dion Hair Clinic',
      description: 'DHI Haartransplantation in Mönchengladbach — ohne Rasur des Oberkopfes. Saphir-DHI Hybrid Methode (+800€) oder Long Hair FUE komplett ohne Rasur (+3.000€). Natürlichste Ergebnisse. Kostenlose Beratung.',
      keywords: 'DHI Haartransplantation, DHI Methode, Direct Hair Implantation, Haartransplantation ohne Rasur, Saphir DHI Hybrid, Long Hair FUE, DHI Deutschland, DHI NRW, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/dhi',
    },
    en: {
      title: 'DHI Hair Transplant without Shaving | Sapphire-DHI Hybrid | Dion Hair Clinic',
      description: 'DHI hair transplant in Germany — no shaving required on top. Sapphire-DHI hybrid method or Long Hair FUE completely without shaving. Most natural results. Free consultation.',
      keywords: 'DHI Hair Transplant, DHI Method, Direct Hair Implantation, Hair Transplant without Shaving, Sapphire DHI Hybrid, Long Hair FUE, DHI Germany, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/dhi?lang=en',
    }
  },
  bart: {
    de: {
      title: 'Barttransplantation Deutschland ab 2.999€ | Vollbart, Kontur & Verdichtung | Dion Hair Clinic',
      description: 'Barttransplantation (Barthaartransplantation) ab 2.999€ in Mönchengladbach. Vollbart, Kontur, Verdichtung & Lücken. FUE Saphir & DHI. Ratenzahlung ab 53€/Monat. Kostenlose Beratung.',
      keywords: 'Barttransplantation, Barthaartransplantation, Barttransplantation Deutschland, Barttransplantation NRW, Barttransplantation Kosten, Vollbart Transplantation, Bartkorrektur, Bartwuchs, lückenhafter Bart, Bart auffüllen, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/bart',
    },
    en: {
      title: 'Beard Transplant Germany from 2,999€ | Full Beard & Correction | Dion Hair Clinic',
      description: 'Beard transplant from 2,999€ in Germany. Full beard, contour, densification & patches. FUE Sapphire & DHI. Installments from 53€/month. Free consultation.',
      keywords: 'Beard Transplant, Beard Transplant Germany, Beard Hair Transplant, Full Beard Transplant, Beard Correction, Patchy Beard, Beard Restoration, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/bart?lang=en',
    }
  },
  augenbrauen: {
    de: {
      title: 'Augenbrauentransplantation Deutschland ab 4.499€ | Long Hair FUE ohne Rasur | Dion Hair Clinic',
      description: 'Augenbrauentransplantation zum Festpreis von 4.499€ in Mönchengladbach. Long Hair FUE komplett ohne Rasur — dauerhafte Alternative zu Microblading. Ratenzahlung ab 78€/Monat. Kostenlose Beratung.',
      keywords: 'Augenbrauentransplantation, Augenbrauentransplantation Deutschland, Augenbrauentransplantation Kosten, Augenbrauen Transplantation, Augenbrauen verdichten, Alternative Microblading, Augenbrauen auffüllen, Augenbrauen Long Hair FUE, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/augenbrauen',
    },
    en: {
      title: 'Eyebrow Transplant Germany 4,499€ | Long Hair FUE No Shave | Dion Hair Clinic',
      description: 'Eyebrow transplant at a fixed price of 4,499€ in Germany. Long Hair FUE completely without shaving — permanent alternative to microblading. Installments from 78€/month. Free consultation.',
      keywords: 'Eyebrow Transplant, Eyebrow Transplant Germany, Eyebrow Transplant Cost, Eyebrow Restoration, Microblading Alternative, Eyebrow Enhancement, Dion Hair Clinic',
      canonical: 'https://dionhairclinic.de/augenbrauen?lang=en',
    }
  }
};

// Service-specific structured data for MedicalProcedure schema
const serviceSchema = {
  allgemein: {
    de: { name: 'Haartransplantation', procedureType: 'Surgical', bodyLocation: 'Kopfhaut', description: 'Professionelle Haartransplantation mit FUE und DHI Methoden in Mönchengladbach. Natürliche Ergebnisse mit über 95% Anwachsrate.' },
    en: { name: 'Hair Transplant', procedureType: 'Surgical', bodyLocation: 'Scalp', description: 'Professional hair transplant with FUE and DHI methods in Germany. Natural results with over 95% growth rate.' }
  },
  koeln: {
    de: { name: 'Haartransplantation Köln', procedureType: 'Surgical', bodyLocation: 'Kopfhaut', description: 'Haartransplantation nahe Köln mit modernsten FUE und DHI Techniken.' },
    en: { name: 'Hair Transplant Cologne', procedureType: 'Surgical', bodyLocation: 'Scalp', description: 'Hair transplant near Cologne with state-of-the-art FUE and DHI techniques.' }
  },
  dhi: {
    de: { name: 'DHI Haartransplantation ohne Rasur', procedureType: 'Surgical', bodyLocation: 'Kopfhaut', description: 'DHI Methode - Direkte Haarimplantation ohne Rasur mit Choi-Pen für natürlichste Ergebnisse.' },
    en: { name: 'DHI Hair Transplant without Shaving', procedureType: 'Surgical', bodyLocation: 'Scalp', description: 'DHI method - Direct hair implantation without shaving using Choi-Pen for the most natural results.' }
  },
  bart: {
    de: { name: 'Barttransplantation', procedureType: 'Surgical', bodyLocation: 'Gesicht / Bart', description: 'Professionelle Barttransplantation für Vollbart, Kinnbart oder Bartkorrekturen mit FUE und DHI Technik.' },
    en: { name: 'Beard Transplant', procedureType: 'Surgical', bodyLocation: 'Face / Beard', description: 'Professional beard transplant for full beard or beard corrections with FUE and DHI technique.' }
  },
  augenbrauen: {
    de: { name: 'Augenbrauentransplantation', procedureType: 'Surgical', bodyLocation: 'Augenbrauen', description: 'Augenbrauentransplantation für natürlich volle Augenbrauen. Dauerhafte Alternative zu Microblading.' },
    en: { name: 'Eyebrow Transplant', procedureType: 'Surgical', bodyLocation: 'Eyebrows', description: 'Eyebrow transplant for naturally full eyebrows. Permanent alternative to microblading.' }
  }
};

// Breadcrumb labels per variant
const breadcrumbLabels = {
  allgemein: { de: 'Haartransplantation', en: 'Hair Transplant' },
  koeln: { de: 'Haartransplantation Köln', en: 'Hair Transplant Cologne' },
  dhi: { de: 'DHI Methode', en: 'DHI Method' },
  bart: { de: 'Barttransplantation', en: 'Beard Transplant' },
  augenbrauen: { de: 'Augenbrauentransplantation', en: 'Eyebrow Transplant' }
};

const SEO = ({ variant = 'allgemein' }) => {
  const { language } = useLanguage();
  const data = useLandingPageData();
  const config = seoConfig[variant]?.[language] || seoConfig.allgemein[language];
  const baseUrl = 'https://dionhairclinic.de';
  const path = variant === 'allgemein' ? '' : `/${variant}`;
  
  const deUrl = `${baseUrl}${path}`;
  const enUrl = `${baseUrl}${path}${path ? '?lang=en' : '?lang=en'}`;

  // Generate BreadcrumbList structured data
  const breadcrumbData = useMemo(() => {
    const label = breadcrumbLabels[variant]?.[language] || breadcrumbLabels.allgemein[language];
    const items = [
      { "@type": "ListItem", "position": 1, "name": "Dion Hair Clinic", "item": baseUrl }
    ];
    if (variant !== 'allgemein') {
      items.push({ "@type": "ListItem", "position": 2, "name": label, "item": config.canonical });
    }
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items
    };
  }, [variant, language, config.canonical]);

  // Generate MedicalProcedure structured data
  const medicalProcedureData = useMemo(() => {
    const service = serviceSchema[variant]?.[language] || serviceSchema.allgemein[language];
    return {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      "name": service.name,
      "procedureType": `https://schema.org/${service.procedureType}`,
      "bodyLocation": service.bodyLocation,
      "description": service.description,
      "howPerformed": language === 'de'
        ? "Entnahme einzelner Haarfollikel und präzise Implantation unter lokaler Betäubung"
        : "Extraction of individual hair follicles and precise implantation under local anesthesia",
      "preparation": language === 'de'
        ? "Kostenlose Beratung und individuelle Behandlungsplanung"
        : "Free consultation and individual treatment planning",
      "followup": language === 'de'
        ? "Regelmäßige Kontrolltermine und 12 Monate Nachbetreuung"
        : "Regular follow-up appointments and 12 months aftercare",
      "status": "https://schema.org/EventScheduled",
      "url": config.canonical,
      "provider": {
        "@type": "MedicalBusiness",
        "name": "Dion Hair Clinic",
        "url": baseUrl,
        "telephone": "+49 2161 2963017",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Schürenweg 61",
          "addressLocality": "Mönchengladbach",
          "postalCode": "41063",
          "addressCountry": "DE"
        }
      }
    };
  }, [variant, language, config.canonical]);

  // Generate FAQPage structured data from mock data
  const faqData = useMemo(() => {
    const faqItems = data?.faqItems;
    if (!faqItems || faqItems.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
  }, [data?.faqItems]);

  // Directly update document.title and meta tags for SPA
  useEffect(() => {
    document.title = config.title;
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', config.description);
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute('content', config.keywords);
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', config.canonical);
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', config.title);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', config.description);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', config.canonical);
    
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', config.title);
    
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', config.description);
    
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    if (twUrl) twUrl.setAttribute('content', config.canonical);
    
    const hreflangDe = document.querySelector('link[hreflang="de"]');
    if (hreflangDe) hreflangDe.setAttribute('href', deUrl);
    
    const hreflangEn = document.querySelector('link[hreflang="en"]');
    if (hreflangEn) hreflangEn.setAttribute('href', enUrl);
    
    const hreflangDefault = document.querySelector('link[hreflang="x-default"]');
    if (hreflangDefault) hreflangDefault.setAttribute('href', deUrl);
    
  }, [config, deUrl, enUrl]);

  // Inject structured data JSON-LD scripts directly into <head>
  useEffect(() => {
    // Remove previously injected dynamic schemas
    document.querySelectorAll('script[data-dynamic-ld]').forEach(el => el.remove());

    const schemas = [breadcrumbData, medicalProcedureData];
    if (faqData) schemas.push(faqData);

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic-ld', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-dynamic-ld]').forEach(el => el.remove());
    };
  }, [breadcrumbData, medicalProcedureData, faqData]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{config.title}</title>
      <meta name="title" content={config.title} />
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      <link rel="canonical" href={config.canonical} />
      
      {/* Language */}
      <html lang={language} />
      <meta name="language" content={language === 'de' ? 'German' : 'English'} />
      
      {/* hreflang for multilingual SEO */}
      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={config.canonical} />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:site_name" content="Dion Hair Clinic" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={config.canonical} />
      <meta property="twitter:title" content={config.title} />
      <meta property="twitter:description" content={config.description} />
      <meta property="twitter:image" content={`${baseUrl}/og-image.jpg`} />
    </Helmet>
  );
};

export default SEO;
